/**
 * Karmana Analytics — fire-and-forget event tracking.
 * Never throws, never blocks the UI.
 */

const SESSION_KEY         = 'karmana_session_id';
const UTM_KEY             = 'karmana_utm';
const G_OVERRIDE_KEY      = 'karmana_g';
const TEST_FLAG           = 'karmana_test';
const ATTRIBUTED_USER_KEY = 'karmana_attributed_user_id';
const ENTRY_PAGE_KEY      = 'karmana_entry_page';

// ── Module-level user & page state ───────────────────────────────────────────
let _currentUserId = null;
let _currentPage   = null;
let _sourcePage    = null;

/** Call after successful sign-in (pass user.id) and on logout (pass null). */
export const setAnalyticsUserId = (id) => {
  _currentUserId = id || null;
};

/** Maps a URL pathname to a canonical page name. */
const _canonicalPage = (pathname) => {
  if (!pathname || pathname === '/') return 'home';
  if (pathname === '/activities') return 'activities';
  if (pathname.startsWith('/activities/')) return 'activity_detail';
  if (pathname === '/organizations') return 'organizations';
  if (pathname.startsWith('/organizations/invite/')) return 'org_invite';
  if (pathname.startsWith('/organizations/')) return 'organisation_detail';
  if (pathname.startsWith('/join/')) return 'team_invite';
  if (pathname.startsWith('/services/donate-used-items')) return 'donate';
  if (pathname.startsWith('/cutlery-bank')) return 'cutlery';
  if (pathname === '/about') return 'about';
  if (pathname === '/my-impact') return 'my_impact';
  return 'other';
};

/**
 * Call immediately before every page_view track() call.
 * Sets source_page to the previous page and current_page to the new one.
 */
export const setCurrentPage = (page) => {
  _sourcePage  = _currentPage;
  _currentPage = page;
};

// ── Internal / test-mode detection ──────────────────────────────────────────
// If ?karmana_test=1 is in the URL, persist the flag to localStorage so all
// future visits from this browser also suppress tracking (until manually cleared).
(() => {
  try {
    const p = new URLSearchParams(window.location.search);
    if (p.get('karmana_test') === '1') localStorage.setItem(TEST_FLAG, '1');
  } catch {}
})();

const isTestDevice = () => {
  try { return localStorage.getItem(TEST_FLAG) === '1'; } catch { return false; }
};

const ensureSession = () => {
  let id = sessionStorage.getItem(SESSION_KEY);
  const p = new URLSearchParams(window.location.search);
  const gCode    = p.get('g');
  const uidParam = p.get('uid');

  if (!id) {
    // New session: create an ID and capture all UTM params from the landing URL.
    id = (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now());
    sessionStorage.setItem(SESSION_KEY, id);

    // Capture canonical entry page once for this session.
    sessionStorage.setItem(ENTRY_PAGE_KEY, _canonicalPage(window.location.pathname));

    // Email attribution: store uid in sessionStorage (no auto-sign-in).
    if (uidParam) {
      sessionStorage.setItem(ATTRIBUTED_USER_KEY, uidParam);
    }

    if (gCode) {
      // WhatsApp tracking link — set source to whatsapp + store g_param
      sessionStorage.setItem(UTM_KEY, JSON.stringify({
        utm_source:   'whatsapp',
        utm_medium:   'group',
        utm_campaign: gCode,
        utm_content:  null,
        utm_term:     null,
        g_param:      gCode,
      }));
    } else {
      sessionStorage.setItem(UTM_KEY, JSON.stringify({
        utm_source:   p.get('utm_source')   || null,
        utm_medium:   p.get('utm_medium')   || null,
        utm_campaign: p.get('utm_campaign') || null,
        utm_content:  p.get('utm_content')  || null,
        utm_term:     p.get('utm_term')     || null,
        g_param:      null,
      }));
    }
  } else if (gCode) {
    // Existing session but URL carries a WhatsApp ?g= code — always re-attribute.
    sessionStorage.setItem(UTM_KEY, JSON.stringify({
      utm_source:   'whatsapp',
      utm_medium:   'group',
      utm_campaign: gCode,
      utm_content:  null,
      utm_term:     null,
      g_param:      gCode,
    }));
  } else if (p.get('utm_source')) {
    // Existing session but the URL carries a utm_source (e.g., user clicked a
    // share link while they already had a tab open). Override stored UTMs so
    // all subsequent events carry the correct attribution (e.g. app_share).
    sessionStorage.setItem(UTM_KEY, JSON.stringify({
      utm_source:   p.get('utm_source')   || null,
      utm_medium:   p.get('utm_medium')   || null,
      utm_campaign: p.get('utm_campaign') || null,
      utm_content:  p.get('utm_content')  || null,
      utm_term:     p.get('utm_term')     || null,
      g_param:      null,
    }));
  }

  return id;
};

const getUtm = () => {
  try { return JSON.parse(sessionStorage.getItem(UTM_KEY) || '{}'); }
  catch { return {}; }
};

const getDeviceInfo = () => {
  const ua = navigator.userAgent;
  let device_type = 'desktop';
  if (/Mobi|Android/i.test(ua) && !/Tablet|iPad/i.test(ua)) device_type = 'mobile';
  else if (/Tablet|iPad/i.test(ua)) device_type = 'tablet';

  let os = 'Other';
  if (/iPad|iPhone|iPod/.test(ua)) os = 'iOS';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/Windows/.test(ua)) os = 'Windows';
  else if (/Macintosh|Mac OS/.test(ua)) os = 'Mac';
  else if (/Linux/.test(ua)) os = 'Linux';

  return { device_type, os };
};

const BASE = process.env.REACT_APP_BACKEND_URL;

export const track = (event_type, data = {}) => {
  try {
    if (isTestDevice()) return;   // ← suppress all events on test devices
    const sessionId = ensureSession();
    const payload = {
      event_type,
      session_id:      sessionId,
      user_id:         _currentUserId || sessionStorage.getItem(ATTRIBUTED_USER_KEY) || null,
      entry_page:      sessionStorage.getItem(ENTRY_PAGE_KEY) || null,
      source_page:     _sourcePage || null,
      is_authenticated: !!_currentUserId,
      ...getUtm(),
      ...getDeviceInfo(),
      ...data,
    };
    fetch(`${BASE}/api/analytics/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).catch(() => {});
  } catch {
    // Analytics must never break the app
  }
};

// ── Session time tracking ────────────────────────────────────────────────────
// Tracks total time on site and active time (tab visible).
// Fires a `session_time` event when the tab goes hidden or the page unloads.
(() => {
  try {
    if (isTestDevice()) return;

    const startedAt = Date.now();
    let activeMs = 0;
    let lastVisibleAt = document.hidden ? null : Date.now();
    let flushed = false;

    const flush = (trigger) => {
      if (flushed) return;
      flushed = true;
      if (lastVisibleAt) activeMs += Date.now() - lastVisibleAt;
      const secondsTotal  = Math.round((Date.now() - startedAt) / 1000);
      const secondsActive = Math.round(activeMs / 1000);
      track('session_time', { seconds_total: secondsTotal, seconds_active: secondsActive, trigger });
    };

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        if (lastVisibleAt) { activeMs += Date.now() - lastVisibleAt; lastVisibleAt = null; }
        flush('tab_hidden');
        flushed = false; // allow re-flush when tab comes back and leaves again
      } else {
        lastVisibleAt = Date.now();
      }
    });

    window.addEventListener('pagehide', () => flush('page_exit'), { once: true });
  } catch {}
})();
